import type { PlanGroup, PartnerSummary } from '@ctct/domain-layer';
import { navigationApi } from '@ctct/infrastructure-layer';
import type { PayflowTopBarPageStore } from '@ctct/state-client-layer';

import type { TEventSource } from '../../../helpers';
import { NON_INTERACTIVE_TOP_BAR_PUIDS } from '../constants';
import { payflowTopBarEventBus } from '../PayflowTopBarEventBus';
import type { ReturnUrlAdapter } from '../types';

export class PayflowTopBarService {
  constructor(
    private pageStore: PayflowTopBarPageStore,
    private urlAdapter: ReturnUrlAdapter,
    private planGroup: PlanGroup,
    private partnerSummary: PartnerSummary,
    private flags?: {
      isMWebVariantEnabled?: boolean;
      isDesktopExitHidden?: boolean;
    }
  ) {}

  public api() {
    return {
      getReturnUrl: () => this.pageStore.getState().returnUrl,
      getReturnLabel: () => this.pageStore.getState().returnLabel,
    };
  }

  public data() {
    return {
      hasReturnUrl: () => !!this.pageStore.getState().returnUrl,
      isNonInteractive: () => this.isNonInteractiveByPUID(),
      isMWebVariantEnabled: () => !!this.flags?.isMWebVariantEnabled,
      isDesktopExitHidden: () => !!this.flags?.isDesktopExitHidden,
    };
  }

  public handlers() {
    return {
      onLogoClick: () => this.handleLogoClick('user'),
      onExitClick: () => this.handleExitClick('user'),
    };
  }

  public onMount() {
    // Initialize returnUrl from URL params
    const { returnUrl, deleteReturnUrl, returnLabel, deleteReturnLabel } = this.urlAdapter.getReturnUrlParams();

    if (returnUrl && this.isValidReturnUrl(returnUrl)) {
      this.pageStore.setState({ returnUrl });

      deleteReturnUrl();

      if (returnLabel) {
        this.pageStore.setState({ returnLabel });
        deleteReturnLabel();
      }
    }
  }

  // Determines if the payflow top bar should be non-interactive, we want to make it non-interactive for trial child accounts under restricted PUIDs
  private isNonInteractiveByPUID(): boolean {
    if (!this.planGroup.isTrialer()) {
      return false;
    }

    return NON_INTERACTIVE_TOP_BAR_PUIDS.includes(this.partnerSummary.getPartnerUID());
  }

  private handleLogoClick(source: TEventSource) {
    this.exitPayflow();
    payflowTopBarEventBus.emit('logoClicked', { source });
  }

  private handleExitClick(source: TEventSource) {
    this.exitPayflow();
    payflowTopBarEventBus.emit('exitClicked', { source });
  }

  // we need to check if the return url is in constantcontact domain
  private isValidReturnUrl(returnUrl: string) {
    try {
      const url = new URL(returnUrl);

      // Ensure HTTPS protocol
      if (url.protocol !== 'https:') {
        return false;
      }

      // Create regex pattern to match any subdomain (including nested) of constantcontact.com
      // Pattern: https://[optional-subdomain(s)]constantcontact.com/[path]
      const hostnamePattern = /^(([a-zA-Z0-9-]+\.)*)?constantcontact\.com$/;

      if (!hostnamePattern.test(url.hostname)) {
        return false;
      }

      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
    } catch (error) {
      // Invalid URL format
      return false;
    }
  }

  private exitPayflow() {
    const { returnUrl } = this.pageStore.getState();

    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      navigationApi.goToDashboard();
    }
  }
}
